import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { cx, usePortalContainer } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";
import { Spinner } from "./Spinner.tsx";

export interface PaletteItem {
  id: string;
  label: string;
  /** Second line: a class, an email, a role. */
  detail?: string;
  icon?: IconName;
  /** Replaces the icon: an avatar or a photo. */
  lead?: ReactNode;
  onSelect: () => void;
}
export interface PaletteGroup { id: string; label: string; items: PaletteItem[] }

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (q: string) => void;
  /** Already filtered for `query`; empty groups are left out. */
  groups: PaletteGroup[];
  placeholder?: string;
  /** Something is still loading (results may grow). */
  busy?: boolean;
  /** Shown when nothing matches. */
  empty?: ReactNode;
  /** Shown under the results: keyboard hints. */
  footer?: ReactNode;
}

/**
 * Global search: a field and one list of results in groups, driven by the keyboard (arrows, Enter,
 * Escape) as a combobox with a listbox, so screen readers follow the highlighted result.
 */
export function CommandPalette({ open, onOpenChange, query, onQueryChange, groups, placeholder = "Search", busy, empty, footer }: CommandPaletteProps) {
  const container = usePortalContainer();
  const listId = useId();
  const shown = useMemo(() => groups.filter((g) => g.items.length > 0), [groups]);
  const flat = useMemo(() => shown.flatMap((g) => g.items), [shown]);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => setActive(0), [query, open]);
  const at = Math.min(active, Math.max(0, flat.length - 1));
  const optionId = (i: number) => `${listId}-${i}`;
  useEffect(() => { listRef.current?.querySelector(`[id="${optionId(at)}"]`)?.scrollIntoView({ block: "nearest" }); });
  const choose = (item: PaletteItem | undefined) => { if (!item) return; onOpenChange(false); item.onSelect(); };
  let n = -1;
  return (
    <BaseDialog.Root open={open} onOpenChange={(o) => onOpenChange(o)}>
      <BaseDialog.Portal container={container}>
        <BaseDialog.Backdrop className="pmu-backdrop pmu-palette__backdrop" />
        <BaseDialog.Popup className="pmu-popup pmu-palette" aria-label="Search">
          <div className="pmu-palette__field">
            <Icon name="search" />
            <input
              autoFocus className="pmu-palette__input" type="text" role="combobox" aria-expanded="true" aria-controls={listId} aria-autocomplete="list"
              aria-activedescendant={flat.length ? optionId(at) : undefined} placeholder={placeholder} value={query} spellCheck={false} autoComplete="off"
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setActive((at + 1) % Math.max(1, flat.length)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setActive((at - 1 + flat.length) % Math.max(1, flat.length)); }
                else if (e.key === "Enter") { e.preventDefault(); choose(flat[at]); }
              }}
            />
            {busy && <Spinner size="sm" label="Searching" />}
            <kbd className="pmu-palette__esc">Esc</kbd>
          </div>
          <div ref={listRef} id={listId} role="listbox" aria-label="Results" className="pmu-palette__list">
            {shown.length === 0 && !busy && <p className="pmu-palette__empty">{empty ?? "Nothing found."}</p>}
            {shown.map((g) => (
              <div key={g.id} role="group" aria-label={g.label} className="pmu-palette__group">
                <div className="pmu-palette__group-label" aria-hidden="true">{g.label}</div>
                {g.items.map((item) => {
                  const i = ++n;
                  return (
                    <div key={item.id} id={optionId(i)} role="option" aria-selected={i === at} className={cx("pmu-palette__item", i === at && "pmu-palette__item--active")}
                      onMouseMove={() => i !== at && setActive(i)} onClick={() => choose(item)}>
                      <span className="pmu-palette__lead">{item.lead ?? <Icon name={item.icon ?? "list"} />}</span>
                      <span className="pmu-palette__text"><span className="pmu-palette__label">{item.label}</span>{item.detail && <span className="pmu-palette__detail">{item.detail}</span>}</span>
                      {i === at && <span className="pmu-palette__enter" aria-hidden="true">↵</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {footer && <div className="pmu-palette__footer">{footer}</div>}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
